import React, { useEffect, useState } from 'react';
import { Flex, Popconfirm } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import FilterBar from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 50 },
};


const UsersTable = () => {

  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Frontend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [allUsers, setAllUsers] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await actions.loadAllUsers(searchTerm);
      if (response.status === 200) {
        const data = response.data;
        setAllUsers(data.results || []);
        setTotalRows(data.count || data.results?.length || 0);
      }
    } catch (e) {
      console.error('Error fetching users:', e);
    }
    setLoading(false);
  };

  // Frontend pagination logic
  const paginateData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = allUsers.slice(startIndex, endIndex);
    setDataSource(paginatedData);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1);
    }
  };


  useEffect(() => {
    fetchAllUsers();
  }, [searchTerm]);

  // Apply frontend pagination when data or page changes
  useEffect(() => {
    paginateData();
  }, [allUsers, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    const response = await actions.deleteUsers(newData.id);
    console.log(response);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchAllUsers();
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
  }


  const defaultColumns = [
    {
      title: 'FIRST NAME',
      dataIndex: 'first_name',
      key: 'first_name',
      sorter: (a, b) => a?.first_name.localeCompare(b?.first_name),
    },
    {
      title: 'LAST NAME',
      dataIndex: 'last_name',
      key: 'last_name',
      sorter: (a, b) => a?.last_name.localeCompare(b?.last_name),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a?.email.localeCompare(b?.email),
    },
    {
      title: 'Actions',
      dataIndex: 'edit',
      key: 'edit',
      render: (_, record) => {
        // console.log(record);
        return dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <a>
              <EditOutlined
                onClick={() => navigate(`/users/edit/${record.id}/`)}
                style={{
                  verticalAlign: 'middle',
                }}
              />
            </a>
            <Popconfirm title="Sure to delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          </Flex>
        ) : null
      }
    },
  ];

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };


  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState('checkbox');

  return (
    <div style={{ padding: 10 }}>
      <Flex gap='large' justify='space-between'>
        <Flex gap='large' align='center' wrap>
          <div>
            Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} users
          </div>

          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search users"
            onRefresh={() => fetchAllUsers()}
            refreshing={loading}
            addLabel="Add User"
            addIcon={<UserAddOutlined />}
            onAdd={() => navigate("/users/new")}
          />
        </Flex>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={row => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['10', '25', '50', '100'],
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
          scroll={{ y: `calc(100vh - 350px)` }}
        />
      </div>
    </div>
  );
};
export default UsersTable;